import { useState, useEffect } from 'react';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { getReportBranchHeaders } from '../utils/branchStorage';
import { getPaymentMethodLabel } from '../utils/paymentMethod';

const baht = (n) => (Number(n) || 0).toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const DAY_OPTIONS = [7, 14, 30];

function Kpi({ label, value, unit, hint }) {
  return (
    <div className="card" style={{ display: 'grid', gap: '0.35rem', alignContent: 'start' }}>
      <div style={{ fontSize: '0.9rem', color: '#d4d7e0', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '1.9rem', fontWeight: 700, color: '#8b9dff', lineHeight: 1.1 }}>
        {value}
        {unit && <span style={{ fontSize: '0.9rem', fontWeight: 500, marginLeft: '0.3rem', color: '#c5c9d3' }}>{unit}</span>}
      </div>
      {hint && <div style={{ fontSize: '0.8rem', color: '#b0b4c0' }}>{hint}</div>}
    </div>
  );
}

function PaymentBreakdown({ byPaymentMethod }) {
  const entries = Object.entries(byPaymentMethod || {});
  if (entries.length === 0) return null;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: '0.75rem'
    }}>
      {entries.map(([method, { total, count }]) => (
        <div
          key={method}
          style={{
            padding: '1rem',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <div style={{ color: '#d4d7e0', fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.35rem' }}>
            {getPaymentMethodLabel(method)}
          </div>
          <div style={{ color: '#fff', fontSize: '1.15rem', fontWeight: 700 }}>
            {baht(total)} ฿
          </div>
          <div style={{ color: '#b0b4c0', fontSize: '0.8rem' }}>
            {count} รายการ
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DailySummary({ apiUrl }) {
  const [today, setToday] = useState(null);
  const [recent, setRecent] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadToday();
  }, []);

  useEffect(() => {
    loadRecent();
  }, [days]);

  const loadToday = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetchWithRetry(`${apiUrl}/api/daily-summary/today`, {
        headers: { 'Authorization': `Bearer ${token}`, ...getReportBranchHeaders() }
      });
      if (response.ok) {
        const result = await response.json();
        setToday(result.data);
      }
    } catch (err) {
      console.error('Failed to load today stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRecent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetchWithRetry(`${apiUrl}/api/daily-summary/recent?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}`, ...getReportBranchHeaders() }
      });
      if (response.ok) {
        const result = await response.json();
        setRecent(result.data || []);
      }
    } catch (err) {
      console.error('Failed to load recent summaries:', err);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <div>กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>📅 สรุปยอดประจำวัน</h2>
      </div>

      {/* Today (real-time) */}
      <h3 style={{ margin: '0 0 1rem' }}>วันนี้ ({today ? formatDate(today.date) : '-'})</h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <Kpi label="ยอดขายรวม" value={baht(today?.totalRevenue)} unit="฿" />
        <Kpi label="จำนวนบิล" value={today?.totalTransactions ?? 0} unit="บิล" />
        <Kpi label="ลูกค้ารวม" value={today?.totalCustomers ?? 0} unit="คน" hint={`ผู้ใหญ่ ${today?.totalAdults ?? 0} / เด็ก ${today?.totalChildren ?? 0}`} />
        <Kpi label="เฉลี่ยต่อบิล" value={baht(today?.avgOrderValue)} unit="฿" />
      </div>

      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>สรุปตามวิธีชำระเงิน (วันนี้)</h3>
        {today && Object.keys(today.byPaymentMethod || {}).length > 0
          ? <PaymentBreakdown byPaymentMethod={today.byPaymentMethod} />
          : <div style={{ color: '#888', fontSize: '0.9rem' }}>ยังไม่มีรายการวันนี้</div>}
      </div>

      {/* History */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h3 style={{ margin: 0 }}>ประวัติย้อนหลัง</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {DAY_OPTIONS.map(n => (
            <button
              key={n}
              onClick={() => setDays(n)}
              className={days === n ? 'button' : 'button-secondary'}
            >
              {n} วัน
            </button>
          ))}
        </div>
      </div>

      {recent.length === 0 ? (
        <div className="card" style={{ color: '#888' }}>
          ยังไม่มีสรุปย้อนหลัง — ระบบจะบันทึกสรุปของแต่ละวันอัตโนมัติตอนเที่ยงคืน
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          {/* table (no className) falls back to index.css's global light-theme `table { background:
              white }` rule otherwise — every <tr> needs its own explicit dark background to sit
              correctly on this page's dark theme, same pattern as TransactionHistory.jsx's table */}
          <table className="table-dark" style={{ width: '100%', borderCollapse: 'collapse', background: 'transparent' }}>
            <thead>
              <tr style={{ backgroundColor: '#1e1e2e', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={tableHeaderStyle}>วันที่</th>
                <th style={tableHeaderStyle}>ยอดขายรวม</th>
                <th style={tableHeaderStyle}>บิล</th>
                <th style={tableHeaderStyle}>ลูกค้า</th>
                <th style={tableHeaderStyle}>ตามวิธีชำระเงิน</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((row, index) => (
                <tr
                  key={row.id}
                  style={{
                    backgroundColor: index % 2 === 0 ? 'rgba(30, 30, 46, 0.8)' : 'rgba(39, 41, 61, 0.8)',
                    borderBottom: '1px solid rgba(255,255,255,0.05)'
                  }}
                >
                  <td style={tableCellStyle}>{formatDate(row.date)}</td>
                  <td style={{ ...tableCellStyle, fontWeight: 700 }}>{baht(row.totalRevenue)} ฿</td>
                  <td style={tableCellStyle}>{row.totalTransactions}</td>
                  <td style={tableCellStyle}>{row.totalCustomers}</td>
                  <td style={{ ...tableCellStyle, fontSize: '0.85rem', color: '#c5c9d3' }}>
                    {Object.entries(row.byPaymentMethod || {})
                      .map(([method, { total }]) => `${getPaymentMethodLabel(method)} ${baht(total)}฿`)
                      .join(' · ') || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// index.css ทาพื้นไล่สีอ่อนให้ `table th` โดยตรง ซึ่งทับพื้นมืดที่ตั้งไว้ที่ <tr>
// ตารางธีมมืดจึงต้องทับพื้นที่ th เอง ไม่งั้นหัวตารางสีอ่อนจะจมหายไปบนพื้นสว่าง
const tableHeaderStyle = {
  textAlign: 'left',
  padding: '0.75rem',
  background: '#1e1e2e',
  color: '#d4d7e0',
  fontSize: '0.85rem',
  fontWeight: 700
};

const tableCellStyle = {
  padding: '0.75rem',
  color: '#ffffff',
  fontSize: '0.95rem'
};
